import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function AdminGallery() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', alt: '', order: 0 });
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte waehlen Sie eine Bilddatei aus');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Das Bild darf maximal 5MB gross sein');
      return;
    }

    // Create preview and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Url = event.target?.result;
      setPreviewUrl(base64Url);
      setNewImage({ ...newImage, url: base64Url });
    };
    reader.readAsDataURL(file);
  };

  const resetDialog = () => {
    setNewImage({ url: '', alt: '', order: 0 });
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDialogClose = (open) => {
    if (!open) {
      resetDialog();
    }
    setDialogOpen(open);
  };

  const addImage = async () => {
    if (!newImage.url) {
      toast.error('Bitte waehlen Sie ein Bild aus');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/gallery`, {
        ...newImage,
        order: gallery.length
      }, getAuthHeader());
      toast.success('Bild hinzugefügt');
      fetchGallery();
      setDialogOpen(false);
      resetDialog();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/gallery/${id}`, getAuthHeader());
      toast.success('Bild gelöscht');
      fetchGallery();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-gallery-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-['Nunito']">
            Galerie
          </h1>
          <p className="text-[#5F6F75] mt-2">
            Verwalten Sie die Praxisbilder
          </p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-[#F4A261] hover:bg-[#E76F51]"
          data-testid="add-image-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Bild hinzufügen
        </Button>
      </div>

      {/* Gallery Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gallery.map((image) => (
          <Card key={image.id} className="overflow-hidden group" data-testid={`gallery-item-${image.id}`}>
            <div className="aspect-video relative">
              <img 
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300?text=Bild+nicht+gefunden';
                }}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteImage(image.id)}
                  data-testid={`delete-image-${image.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </Button>
              </div>
            </div>
            <CardContent className="p-4">
              <p className="text-sm text-[#5F6F75] truncate">{image.alt || 'Kein Titel'}</p>
            </CardContent>
          </Card>
        ))}

        {gallery.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <ImageIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-[#5F6F75]">Noch keine Bilder vorhanden</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setDialogOpen(true)}
              >
                Erstes Bild hinzufügen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Image Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#264653] font-['Nunito']">
              Neues Bild hinzufügen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File Upload Area */}
            <div className="space-y-2">
              <Label>Bild auswählen</Label>
              <div
                className="border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] rounded-lg p-8 text-center hover:border-[#F4A261] hover:bg-[#FEF6F0] transition-all cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="image-file-input"
                />
                <div className="w-14 h-14 mx-auto mb-3 bg-[#F4A261]/10 rounded-full flex items-center justify-center">
                  <Upload className="h-7 w-7 text-[#F4A261]" />
                </div>
                <p className="text-sm font-medium text-[#264653]">
                  Klicken Sie hier um ein Bild auszuwählen
                </p>
                <p className="text-xs text-[#5F6F75] mt-1">
                  JPG, PNG oder GIF (max. 5MB)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alt">Bildbeschreibung (Alt-Text)</Label>
              <Input
                id="alt"
                value={newImage.alt}
                onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
                placeholder="z.B. Therapieraum"
                data-testid="image-alt-input"
              />
            </div>

            {previewUrl && (
              <div className="border rounded-lg overflow-hidden">
                <p className="text-sm text-[#5F6F75] p-2 bg-gray-50">Vorschau:</p>
                <img
                  src={previewUrl}
                  alt="Vorschau"
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Abbrechen</Button>
            </DialogClose>
            <Button
              onClick={addImage}
              className="bg-[#F4A261] hover:bg-[#E76F51]"
              data-testid="save-image-btn"
              disabled={uploading || !newImage.url}
            >
              {uploading ? 'Wird hochgeladen...' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
